import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../components/ui/accordion';
import {
  ArrowRight, Search, MapPin, Globe, Ticket, CheckCircle2, ShieldCheck,
  Zap, TrendingUp, Users, Sparkles, Link2, CalendarCheck, BadgeCheck,
  Plug, LineChart, CreditCard,
} from 'lucide-react';

const H = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const GOOGLE = { blue: '#4285F4', red: '#EA4335', yellow: '#FBBC05', green: '#34A853' };

/* lightweight scroll reveal */
const Reveal = ({ children, className = '', delay = 0, as: Tag = 'div' }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={`reveal ${shown ? 'reveal-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</Tag>;
};

const FAQS = [
  { q: 'What is Google Things to Do?', a: 'Google Things to Do is a discovery surface inside Google Search and Google Maps that shows travellers the tickets, tours and attractions they can book at a destination — right at the moment they are planning a trip. When a shopper searches for a place or attraction, bookable ticket options appear directly in the results.' },
  { q: 'How does StayMii connect my attraction to Google Things to Do?', a: 'StayMii provides a managed connection to Google Things to Do through its integrated distribution network. Your live ticket types and real-time availability are automatically published, eliminating the need to build and maintain your own integration or product feed.' },
  { q: 'Do I need extra technology or a developer?', a: 'No. If you already sell tickets on StayMii E-Ticket, distribution to Google Things to Do is a one-click connection inside your Distribution dashboard. Your prices, ticket types and capacity are synced automatically — no feeds, no code, no developer.' },
  { q: 'How do payments and bookings work?', a: 'Travellers discover your attraction on Google and complete their booking through the connected marketplace. The booking flows straight back into your StayMii dashboard as a confirmed order with a tamper-proof QR e-ticket, exactly like a direct sale — ready to scan at your gate.' },
  { q: 'How much does it cost to distribute on Google Things to Do?', a: 'There is no separate platform fee to switch on Google Things to Do distribution — it is included with StayMii E-Ticket. Standard marketplace / OTA commission applies only on bookings that come through the channel, so you only pay when you actually sell.' },
  { q: 'Will my availability and prices always stay in sync?', a: 'Yes. StayMii is your single source of truth. Ticket types, prices, daily capacity and stop-sells update in real time across every connected channel — so you never oversell or show a stale price on Google.' },
];

const GoogleThingsToDo = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  /* FAQ rich-snippet schema */
  useEffect(() => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-gtd-faq', 'true');
    s.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    });
    document.head.appendChild(s);
    return () => { document.head.querySelector('script[data-gtd-faq]')?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="gtd-page">
      <SEO
        title="Sell Attraction Tickets on Google Things to Do — StayMii Distribution"
        description="Distribute your attraction, tour and activity tickets on Google Things to Do through StayMii. Reach travellers on Google Search & Maps at the moment they plan — real-time availability, one-click connection, no new tech. Built in Malaysia."
        keywords="google things to do, sell tickets on google, google things to do distribution, attraction distribution malaysia, list attraction on google, google travel tickets, tour operator distribution, e-ticket distribution partner"
        canonicalUrl="/google-things-to-do"
      />
      <LandingNavBar />
      <StyleTag />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative overflow-hidden pt-28 pb-24 lg:pt-36 lg:pb-32 bg-gradient-to-b from-slate-50 via-white to-white" data-testid="gtd-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[560px] h-[560px] rounded-full blur-[150px]" style={{ background: `${GOOGLE.blue}25` }} />
          <div className="absolute -bottom-24 -left-20 w-[460px] h-[460px] rounded-full blur-[140px]" style={{ background: `${GOOGLE.green}1f` }} />
          <div className="absolute inset-0 opacity-[0.5]" style={{ backgroundImage: 'linear-gradient(#0f172a0d 1px,transparent 1px),linear-gradient(90deg,#0f172a0d 1px,transparent 1px)', backgroundSize: '48px 48px', maskImage: 'radial-gradient(ellipse at 50% 30%, black, transparent 75%)' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm mb-7 reveal reveal-in" data-testid="gtd-hero-badge">
              <img src="/ota-logos/google-things-to-do.png" alt="Google Things to Do" className="h-4 w-auto" />
              <span className="text-slate-500 text-[11px] font-bold uppercase tracking-[0.2em]" style={MONO}>StayMii Distribution Partner</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-slate-900 mb-6" style={H} data-testid="gtd-hero-title">
              Get your tickets on<br />
              <span className="relative inline-block">
                <span style={{ color: GOOGLE.blue }}>G</span>
                <span style={{ color: GOOGLE.red }}>o</span>
                <span style={{ color: GOOGLE.yellow }}>o</span>
                <span style={{ color: GOOGLE.blue }}>g</span>
                <span style={{ color: GOOGLE.green }}>l</span>
                <span style={{ color: GOOGLE.red }}>e</span>
                <span className="text-slate-900"> Things to Do.</span>
              </span>
            </h1>

            <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-9 font-medium">
              Reach travellers on <span className="font-semibold" style={{ color: GOOGLE.blue }}>Google Search &amp; Maps</span> at the exact moment
              they plan their trip. StayMii distributes your live tickets, prices and availability to Google Things to Do —
              with <span className="text-slate-900 font-semibold">one click</span> and no new technology.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-9">
              <Link to="/auth/signup?category=eticket" data-testid="gtd-hero-cta-signup"
                className="cta-glow group inline-flex items-center justify-center gap-2 px-8 py-4 text-white rounded-xl font-black text-sm tracking-wide transition-colors"
                style={{ backgroundColor: GOOGLE.blue }}>
                Start distributing <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/contact" data-testid="gtd-hero-cta-demo"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 rounded-xl font-semibold text-sm shadow-sm transition-colors">
                Talk to our team
              </Link>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-slate-500 font-medium" data-testid="gtd-hero-trust">
              {['Real-time availability sync', 'No feeds or code', 'Managed connection', 'Pay only when you sell'].map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" style={{ color: GOOGLE.green }} />{t}</span>
              ))}
            </div>
          </div>

          {/* Google-style search result mock */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-full max-w-sm rounded-3xl bg-white border border-slate-200 p-6 shadow-[0_25px_60px_-20px_rgba(15,23,42,0.35)]" data-testid="gtd-hero-graphic">
              <div className="flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 mb-5 shadow-sm">
                <Search className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600" style={MONO}>things to do in penang</span>
              </div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-3" style={MONO}>Tickets · Things to do</p>
              {[
                { n: 'Penang Heritage Museum', p: 'MYR 35', tag: 'Instant confirm' },
                { n: 'Langkawi Island Tour', p: 'MYR 189', tag: 'Free cancellation' },
                { n: 'Sunrise Adventure Park', p: 'MYR 45', tag: 'Best seller' },
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-3 rounded-2xl border border-slate-100 hover:border-slate-200 px-3.5 py-3 mb-2.5 transition-colors">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${GOOGLE.blue}14` }}>
                    <Ticket className="w-5 h-5" style={{ color: GOOGLE.blue }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-900 truncate" style={H}>{r.n}</p>
                    <span className="text-[10px] font-semibold" style={{ color: GOOGLE.green }}>{r.tag}</span>
                  </div>
                  <span className="text-sm font-black text-slate-900 shrink-0" style={MONO}>{r.p}</span>
                </div>
              ))}
              <div className="mt-4 flex items-center gap-2 text-[10px] text-slate-400" style={MONO}>
                <BadgeCheck className="w-4 h-4" style={{ color: GOOGLE.blue }} /> POWERED BY STAYMII DISTRIBUTION
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ MARQUEE ═══════════════ */}
      <div className="border-y border-slate-100 bg-slate-50 py-5 overflow-hidden" data-testid="gtd-marquee">
        <div className="flex items-center gap-10 marquee whitespace-nowrap text-slate-400">
          {['GOOGLE SEARCH', 'GOOGLE MAPS', 'GOOGLE TRAVEL', 'REAL-TIME AVAILABILITY', 'INSTANT CONFIRMATION', 'MANAGED CONNECTION', 'ZERO NEW TECH', 'GOOGLE SEARCH', 'GOOGLE MAPS', 'GOOGLE TRAVEL', 'REAL-TIME AVAILABILITY', 'INSTANT CONFIRMATION', 'MANAGED CONNECTION', 'ZERO NEW TECH'].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-3 text-xs font-bold tracking-[0.2em]" style={MONO}>
              <Zap className="w-3.5 h-3.5" style={{ color: GOOGLE.blue }} />{t}
            </span>
          ))}
        </div>
      </div>

      {/* ═══════════════ WHY ═══════════════ */}
      <section className="py-24 lg:py-32 bg-white" data-testid="gtd-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ ...MONO, color: GOOGLE.blue }}>Meet demand where it starts</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={H}>
              Billions of trips begin on Google. Be there when they do.
            </h2>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Search, t: 'Capture high-intent travellers', d: 'Appear the instant someone searches for your destination or attraction — not after they have already booked elsewhere.', c: GOOGLE.blue },
              { icon: MapPin, t: 'Show up on Google Maps', d: 'Your bookable tickets surface right on the map pin travellers tap when exploring where to go.', c: GOOGLE.red },
              { icon: TrendingUp, t: 'A new demand channel', d: 'Add incremental bookings on top of your direct sales and existing OTAs — with no cannibalisation.', c: GOOGLE.green },
              { icon: Plug, t: 'One-click connection', d: 'Switch on Google Things to Do from your StayMii Distribution dashboard. No feeds, no code, no developer.', c: GOOGLE.yellow },
              { icon: LineChart, t: 'Always-accurate availability', d: 'StayMii is your single source of truth — prices, capacity and stop-sells sync in real time.', c: GOOGLE.blue },
              { icon: CreditCard, t: 'Pay only when you sell', d: 'No platform fee to switch it on. Standard channel commission applies only on bookings you receive.', c: GOOGLE.green },
            ].map((f, i) => (
              <Reveal key={i} delay={(i % 3) * 60}>
                <div className="group h-full rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`gtd-why-${i}`}>
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${f.c}14` }}>
                    <f.icon className="w-5 h-5" style={{ color: f.c }} />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-1.5" style={H}>{f.t}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ HOW IT WORKS ═══════════════ */}
      <section className="py-24 border-y" style={{ backgroundColor: `${GOOGLE.blue}0a`, borderColor: `${GOOGLE.blue}1f` }} data-testid="gtd-how">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-14 max-w-xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ ...MONO, color: GOOGLE.blue }}>How distribution works</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight" style={H}>From your dashboard to Google in four steps</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { n: '01', t: 'Sell on StayMii', d: 'Set up your ticket types, prices and capacity on StayMii E-Ticket.' },
              { n: '02', t: 'Connect the channel', d: 'Open Distribution and connect Google Things to Do with one click.' },
              { n: '03', t: 'We publish for you', d: 'StayMii syncs your live products & availability to Google via our managed connection.' },
              { n: '04', t: 'Receive bookings', d: 'Google shoppers book; orders and QR e-tickets land in your dashboard.' },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="h-full rounded-2xl border bg-white shadow-sm p-7" style={{ borderColor: `${GOOGLE.blue}1f` }} data-testid={`gtd-step-${i}`}>
                  <span className="text-4xl font-black" style={{ ...MONO, color: `${GOOGLE.blue}55` }}>{s.n}</span>
                  <h3 className="text-slate-900 font-bold mt-3 mb-1.5" style={H}>{s.t}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ SINGLE SOURCE OF TRUTH ═══════════════ */}
      <section className="py-24 lg:py-32 bg-white" data-testid="gtd-sync">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ ...MONO, color: GOOGLE.blue }}>One source of truth</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-6" style={H}>
              Manage everything once. Sell everywhere.
            </h2>
            <p className="text-slate-500 leading-relaxed mb-8 max-w-lg">
              StayMii powers a certified distribution connection — the same technology that syncs your tickets to global
              marketplaces now puts you on Google Things to Do. Update a price or pause a rate once, and it reflects
              everywhere instantly. No double entry, no oversells, no stale listings.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: Sparkles, l: 'Live products & pricing' },
                { icon: CalendarCheck, l: 'Real-time capacity & stop-sell' },
                { icon: Users, l: 'Bookings unified in one dashboard' },
                { icon: ShieldCheck, l: 'Tamper-proof QR e-tickets' },
              ].map((u, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white shadow-sm px-4 py-3.5" data-testid={`gtd-sync-${i}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${GOOGLE.blue}14` }}><u.icon className="w-4 h-4" style={{ color: GOOGLE.blue }} /></div>
                  <span className="text-sm font-semibold text-slate-700">{u.l}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="relative rounded-3xl bg-[#0C2159] p-8 shadow-[0_25px_60px_-20px_rgba(12,33,89,0.55)]" data-testid="gtd-sync-graphic">
              <p className="text-[10px] tracking-[0.2em] mb-5" style={{ ...MONO, color: `${GOOGLE.blue}cc` }}>// STAYMII · ONE HUB → MANY CHANNELS</p>
              <div className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3.5 mb-4 flex items-center gap-3">
                <Ticket className="w-5 h-5" style={{ color: GOOGLE.yellow }} />
                <span className="text-sm text-white font-bold" style={H}>StayMii E-Ticket</span>
                <span className="ml-auto text-[10px] text-slate-400" style={MONO}>SOURCE OF TRUTH</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {['Google Things to Do', 'Viator', 'GetYourGuide', 'Klook', 'Trip.com', 'Airbnb'].map((c, i) => (
                  <div key={c} className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
                    <Link2 className="w-3.5 h-3.5 shrink-0" style={{ color: i === 0 ? GOOGLE.blue : '#64748b' }} />
                    <span className={`text-xs truncate ${i === 0 ? 'text-white font-semibold' : 'text-slate-400'}`} style={MONO}>{c}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex items-center gap-2 text-[11px] text-slate-400" style={MONO}>
                <ShieldCheck className="w-4 h-4" style={{ color: GOOGLE.green }} /> SYNCED IN REAL TIME · NO OVERSELL
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════ FAQ ═══════════════ */}
      <section className="py-24 bg-slate-50 border-t border-slate-100" data-testid="gtd-faq">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ ...MONO, color: GOOGLE.blue }}>FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-5" style={H}>Questions, answered</h2>
            <p className="text-slate-500 leading-relaxed mb-6">Everything you need to know about distributing on Google Things to Do with StayMii.</p>
            <div className="inline-flex items-center gap-3 rounded-xl border bg-white shadow-sm px-4 py-3" style={{ borderColor: `${GOOGLE.blue}33` }}>
              <BadgeCheck className="w-5 h-5" style={{ color: GOOGLE.blue }} />
              <span className="text-sm text-slate-700 font-semibold">Certified StayMii distribution partner</span>
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="space-y-3" data-testid="gtd-faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-slate-200 bg-white shadow-sm px-6" data-testid={`gtd-faq-item-${i}`}>
                  <AccordionTrigger className="text-slate-900 font-bold hover:no-underline text-base py-5" style={H}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-500 leading-relaxed text-sm pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* ═══════════════ FINAL CTA ═══════════════ */}
      <section className="relative py-28 overflow-hidden" style={{ background: `linear-gradient(135deg, ${GOOGLE.blue}, ${GOOGLE.green})` }} data-testid="gtd-final-cta">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <Reveal className="relative max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-white items-center justify-center mb-6 shadow-xl p-3">
            <img src="/ota-logos/google-things-to-do.png" alt="Google Things to Do" className="w-full h-auto" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tighter mb-5" style={H}>Put your attraction on Google today</h2>
          <p className="text-white/90 leading-relaxed max-w-lg mx-auto mb-9">
            Join StayMii and reach millions of travellers the moment they plan their trip — with real-time availability
            and one connected dashboard. You only pay when you sell.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/auth/signup?category=eticket" data-testid="gtd-cta-start-bottom"
              className="inline-flex items-center justify-center gap-2 px-9 py-4 bg-white hover:bg-slate-50 rounded-xl font-black text-sm transition-colors shadow-lg" style={{ color: GOOGLE.blue }}>
              Start distributing <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/e-ticket" data-testid="gtd-cta-product-bottom"
              className="inline-flex items-center justify-center gap-2 px-9 py-4 bg-white/15 hover:bg-white/25 text-white border border-white/40 rounded-xl font-semibold text-sm transition-colors">
              Explore StayMii E-Ticket
            </Link>
          </div>
        </Reveal>
      </section>

      <LandingFooter />
    </div>
  );
};

const StyleTag = () => (
  <style>{`
    .reveal{opacity:0;transform:translateY(26px);transition:opacity .7s cubic-bezier(.2,.7,.2,1),transform .7s cubic-bezier(.2,.7,.2,1)}
    .reveal-in{opacity:1;transform:none}
    .cta-glow{animation:ctaPulse 2.8s ease-in-out infinite}
    @keyframes ctaPulse{0%,100%{box-shadow:0 0 0 0 rgba(66,133,244,.35)}50%{box-shadow:0 0 26px 3px rgba(66,133,244,.45)}}
    .marquee{animation:marq 32s linear infinite;width:max-content}
    @keyframes marq{from{transform:translateX(0)}to{transform:translateX(-50%)}}
    @media (prefers-reduced-motion: reduce){
      .reveal{opacity:1;transform:none;transition:none}
      .cta-glow,.marquee{animation:none}
    }
  `}</style>
);

export default GoogleThingsToDo;
